import { DBSchema } from 'idb';
import { injectable } from 'inversify';

import { IdbCacheService } from '@vk-hr-tek/core/idb-cache';
import { Calendar } from '@vk-hr-tek/core/calendar';
import { Logger } from '@vk-hr-tek/core/logger';

import type { CachedFormValues } from '../types';

const EVENT_CACHE_TABLE_NAME = 'events' as const;

interface AttributeToSave {
  id: string;
  value: CachedFormValues;
  timestamp: number;
}

interface EventsDB extends DBSchema {
  [EVENT_CACHE_TABLE_NAME]: {
    key: string;
    value: AttributeToSave | null;
    indexes: { timestamp: number };
  };
}

@injectable()
export class EventCacheService extends IdbCacheService<
  EventsDB,
  typeof EVENT_CACHE_TABLE_NAME
> {
  protected readonly database = 'eventsData';

  protected readonly tableName = EVENT_CACHE_TABLE_NAME;

  constructor(
    logger: Logger,
    private calendar: Calendar,
  ) {
    super(logger);
  }

  public async getValue(id: string, tableName = this.tableName) {
    try {
      const db = await this.init(tableName);

      let cachedAttributeToSave: AttributeToSave | null;

      if (this.memoryDb) {
        cachedAttributeToSave =
          this.memoryDb.get(String(tableName), id) ?? null;
      } else {
        const tx = db?.transaction(tableName, 'readonly');
        const store = tx?.objectStore(tableName);
        cachedAttributeToSave = (await store?.get(id)) || null;
      }

      if (!cachedAttributeToSave) {
        return {};
      }

      const unexpiredCachedValueEntries = Object.entries(
        cachedAttributeToSave.value,
      ).filter(([, attributeValue]) => {
        const isAttributeFileValue =
          typeof attributeValue === 'object' &&
          !Array.isArray(attributeValue) &&
          !!attributeValue.value;

        if (!isAttributeFileValue || typeof attributeValue.value !== 'string') {
          return true;
        }

        const attributeFileValue = attributeValue.value;

        const parsedAttributeFileValue:
          | { fileId: string; expiredAt: string }
          | undefined = attributeFileValue
          ? JSON.parse(attributeFileValue)
          : undefined;
        const parsedExpiredAtOfAttributeToSave: string | undefined =
          parsedAttributeFileValue
            ? parsedAttributeFileValue.expiredAt
            : undefined;

        return (
          !parsedExpiredAtOfAttributeToSave ||
          this.calendar.isMoreThan(
            new Date(parsedExpiredAtOfAttributeToSave),
            new Date(new Date().toISOString()),
          )
        );
      });

      return Object.fromEntries(unexpiredCachedValueEntries);
    } catch (error) {
      this.logger.error(error, {
        tags: {
          vkdoc_error_type: 'idb',
        },
      });
      return null;
    }
  }

  public async saveValue(
    id: string,
    value: AttributeToSave,
    tableName = this.tableName,
  ) {
    try {
      return await this.writeRecord(tableName, { ...value, id });
    } catch (error) {
      this.logger.error(error, {
        tags: {
          vkdoc_error_type: 'idb',
        },
      });
      return null;
    }
  }
}
